import type { InfiniteData } from '@tanstack/react-query';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';

import { useIsExternalUser } from '~/hooks/useIsExternalUser';
import { useWmgSession } from '~/modules/auth/hooks/useWmgSession';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { BFFClient } from '~settings/api/lib/BFFClient';
import { useDropdownPageSize } from '~settings/hooks/useDropdownPageSize';

import { LabelGroupQueryKeys } from '../config/queryKeys';
import { getRequestHeaders } from '../lib/headers';
import { createPaginationQueryOptions } from '../lib/paginationOptions';
import {
  type LabelGroupListResponse,
  LabelGroupListResponseSchema,
} from '../lib/schemas';
import type { PresetUseInfiniteQueryOptions } from '../lib/types';
import type { LabelGroupListQueryVariables } from './labelGroupList';

export type LabelGroupInfiniteListQueryVariables = Omit<
  LabelGroupListQueryVariables,
  'search' | 'skip'
>;

export function useLabelGroupInfiniteList<TData = LabelGroupListResponse>(
  variables: LabelGroupInfiniteListQueryVariables,
  options?: PresetUseInfiniteQueryOptions<
    LabelGroupListResponse,
    InfiniteData<TData>
  >,
) {
  const session = useWmgSession();
  const [labelGroupsEnabled] = useFeatureFlags([
    FEATURE_FLAGS.ContainerSettingsLabelGroups,
  ]);
  const dropdownSize = useDropdownPageSize();
  const isCurrentUserInternal = !useIsExternalUser();
  const { take = dropdownSize, ...restVariables } = variables;

  const { data, ...query } = useInfiniteQuery({
    queryKey: LabelGroupQueryKeys.infiniteList(variables),
    queryFn: async ({ pageParam }) => {
      const responseData = await BFFClient.get('label-groups', {
        headers: getRequestHeaders(session.token),
        searchParams: { ...pageParam, ...restVariables },
      }).json();

      return LabelGroupListResponseSchema.parse(responseData);
    },
    placeholderData: keepPreviousData,
    ...options,
    ...createPaginationQueryOptions({ take }),
    enabled:
      labelGroupsEnabled && isCurrentUserInternal && options?.enabled !== false,
  });

  return { labelGroupInfiniteList: data, ...query };
}
